import { ScreenshotMode } from '@enums/screenshot-mode.enum';
import { ExecutionType } from '@modules/test-development/enums/execution-type.enum';
import { OnAbortedAction } from '@modules/test-development/enums/on-aborted-action.enum';
import { PreRequisiteAction } from '@modules/test-development/enums/pre-requisite-action.enum';
import { ReRunType } from '@modules/test-development/enums/re-run-type.enum';
import { RecoverAction } from '@modules/test-development/enums/recover-action.enum';
import { ResultConstant } from '@modules/test-development/enums/result-constant.enum';
import { Screenshot } from '@modules/test-development/enums/screenshot.enum';
import { TestDevice } from '@testsigmainc/models';
import { immerable } from 'immer';
import {
  custom,
  deserialize,
  list,
  object,
  optional,
  serializable,
  SKIP,
} from 'serializr';
import { ApplicationVersion } from '../../../models/application-version.model';
import { Base } from '../../../models/base.model';
import { PageObject } from '../../../models/page-object.model';
import { DisabledTestCase } from './disabled-test-case.model';
import { ExecutionResult } from './execution-result.model';
import { KibbutzTestPlanHookData } from './kibbutz-test-plan-hook-data.model';
import { KibbutzTestPlanHook } from './kibbutz-test-plan-hook.model';

export class TestPlan extends Base implements PageObject {
  [immerable] = true;
  @serializable
  public applicationVersionId: number;
  @serializable
  public name: string;
  @serializable
  public description: string;
  @serializable(
    custom(
      (v) => {
        if (v) return v.join(',');
      },
      (v) => {
        if (v)
          if (v instanceof Array) return v;
          else return v.split(',');
      },
    ),
  )
  public mailList: string[];
  @serializable
  public stepTimeOut: number;
  @serializable
  public enforceTimeOut: boolean;
  @serializable
  public pageTimeOut: number;
  @serializable
  public environmentId: number;
  @serializable
  public screenshot: Screenshot;
  @serializable
  public screenshotMode: ScreenshotMode;
  @serializable
  public hasHybridLab: boolean;
  @serializable
  public createdById: number;
  @serializable
  public updatedById: number;
  @serializable
  public lastRunId: number;
  @serializable
  public isManual: boolean;

  @serializable(
    custom(
      () => SKIP,
      (v) => v,
    ),
  )
  public entityType: string;

  @serializable(object(ApplicationVersion))
  public applicationVersion: ApplicationVersion;

  @serializable(
    custom(
      () => SKIP,
      (v) => {
        if (v) return new ExecutionResult().deserialize(v);
      },
    ),
  )
  public lastRun: ExecutionResult;
  @serializable(optional())
  public retrySessionCreation: boolean;
  @serializable(optional())
  public retrySessionCreationTimeout: number;
  @serializable
  public msTeamsConnectorNotificationEnabled: boolean;
  @serializable
  public googleChatConnectorNotificationEnabled: boolean;
  @serializable
  public slackConnectorNotificationEnabled: boolean;
  @serializable
  public recoveryAction: RecoverAction;
  @serializable
  public onAbortedAction: OnAbortedAction;
  @serializable
  public onSuitePreRequisiteFail: PreRequisiteAction;
  @serializable
  public onMachinePreRequisiteFail: PreRequisiteAction;
  @serializable
  public reRunType: ReRunType;
  @serializable
  public onTestCasePreRequisiteFail: PreRequisiteAction;
  @serializable
  public onStepPreRequisiteFail: RecoverAction;
  @serializable
  public isAccessibilityTestEnabled: boolean;
  @serializable
  public wcagVersion: string;

  @serializable(
    custom(
      (v) => {
        const results: string[] = [];
        if (v?.length > 0)
          v.forEach((res) => {
            let result: string;
            if (res == ResultConstant.SUCCESS) result = '0';
            else if (res == ResultConstant.FAILURE) result = '1';
            else if (res == ResultConstant.NOT_EXECUTED) result = '3';
            else if (res == ResultConstant.QUEUED) result = '5';
            else if (res == ResultConstant.STOPPED) result = '6';
            else if (res == ResultConstant.RUNNING) result = '9';
            results.push(result);
          });
        return results;
      },
      (v) => {
        const results: ResultConstant[] = [];
        if (v?.length > 0)
          v.forEach((res) => {
            let result: ResultConstant;
            if (res == '0' || res == 'SUCCESS') result = ResultConstant.SUCCESS;
            else if (
              res == '1' ||
              res == 'FAILURE' ||
              res == '2' ||
              res == 'ABORTED' ||
              res == '4' ||
              res == 'PRE_REQUISITE_FAILURE'
            )
              result = ResultConstant.FAILURE;
            else if (res == '3' || res == 'NOT_EXECUTED')
              result = ResultConstant.NOT_EXECUTED;
            else if (res == '5' || res == 'QUEUED')
              result = ResultConstant.QUEUED;
            else if (res == '6' || res == 'STOPPED')
              result = ResultConstant.STOPPED;
            else if (res == '9' || res == 'RUNNING')
              result = ResultConstant.RUNNING;
            results.push(result);
          });
        return results;
      },
    ),
  )
  public notificationStatusList: ResultConstant[];

  @serializable(optional(list(object(TestDevice))))
  public environments: TestDevice[];

  @serializable
  public matchBrowserVersion: boolean;
  @serializable
  public isHeadless: boolean;
  @serializable(
    custom(
      (v) => v,
      (v) => v,
    ),
  )
  public tags: string[];

  @serializable(
    custom(
      (v) => v,
      (v) => v,
    ),
  )
  public selectedNotificationApplications: number[];

  @serializable
  public postHookId: number;

  @serializable(
    custom(
      (v) => v,
      (v) => v,
    ),
  )
  public postPlanHook: KibbutzTestPlanHook;

  @serializable(
    custom(
      (v) => v,
      (v) => v,
    ),
  )
  public hookData: KibbutzTestPlanHookData[];

  @serializable
  public executionType: ExecutionType = ExecutionType.CROSS_BROWSER;

  @serializable(list(object(DisabledTestCase)))
  public disabledTestCases: DisabledTestCase[];

  public suiteIds: number[];

  @serializable(
    optional(
      custom(
        (v) => v,
        (v) => v,
      ),
    ),
  )
  public uftOptionalArguments: string;

  @serializable
  public salesforceEnvironmentId: number;

  deserialize(input: any): this {
    return Object.assign(this, deserialize(TestPlan, input));
  }
}
